import { saveAs } from 'file-saver';
import { NamedBlob, _ } from '@mira-avant/mtlibs';
import { fromEvent as observableFromEvent, Observable } from 'rxjs';
import { first, map, race } from 'rxjs/operators';
import { UrlHelper } from './url.helper';

export const MIMETYPE_X_WAV = 'audio/x-wav';
export const MIMETYPE_WAV = 'audio/wav';
export const MIMETYPE_MPEG = 'audio/mpeg';
export const MIMETYPE_MP3 = 'audio/mp3';
export const MIMETYPE_PDF = 'application/pdf';
export const MIMETYPE_APPLICATION = 'application/octet-stream';

export function isFile(value: any): value is File {
	return value instanceof File;
}

export class FileHelper {
	/**
	 * Save a string to disk as a file
	 *
	 * @static
	 * @param {string} content
	 * @param {string} fileName
	 * @param {string} [mimeType='text/plain;charset=utf-8']
	 */
	static saveString(content: string, fileName: string, mimeType: string = 'text/plain;charset=utf-8'): void {
		const blob = new Blob([content], { type: mimeType });
		const url = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;

		// Append → click → remove (all in the same tick is fine)
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);

		// Very important: free the blob memory
		URL.revokeObjectURL(url);
	}

	static namedBlob(parts?: any[], name: string = 'unknown', options?: BlobPropertyBag): NamedBlob {
		return Object.assign(new Blob(parts, options), { name });
	}

	static saveBlob(blob: Blob, fileName: string): Blob {
		// throw error if link's can't download blobs as files (i.e., safari)
		let a: HTMLAnchorElement = document.createElement('a');
		if (typeof a['download'] != 'undefined') {
			saveAs(blob, fileName);
		} else {
			throw new Error('Browser does not support downloads');
		}
		return blob;
	}

	static saveUrl(url: string, fileName: string = ''): boolean {
		url = UrlHelper.ignoreProtocol(url);
		let a: HTMLAnchorElement = document.createElement('a');
		a.href = url;
		if (typeof a['download'] != 'undefined') {
			a.setAttribute('download', fileName);
		} else {
			a.target = '_blank';
		}
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		return true;
	}

	static saveUrlAsBlob(url: string, fileName: string): Observable<Blob> {
		return FileHelper.readFileUrl(url).pipe(
			map((arr: Uint8Array) => {
				// Ensure the buffer is an ArrayBuffer
				const arrayBuffer = arr.buffer instanceof ArrayBuffer ? arr.buffer : new ArrayBuffer(arr.length);

				return FileHelper.saveBlob(
					new Blob([new Uint8Array(arrayBuffer, arr.byteOffset, arr.length)]),
					fileName
				);
			})
		);
	}

	static readFile(file: File | Blob, readAs?: string): Observable<any> {
		// read file (i.e., input field value)
		let reader: FileReader = new FileReader();
		let load: Observable<Event | any> = observableFromEvent(reader, 'load', (event: Event) => {
			return _.get(event, "target['result']");
		});
		let error: Observable<Event> = observableFromEvent(reader, 'error', () => {
			throw new Error('Could not read ' + _.get(file, 'name', '...') || file['type']);
		});
		switch (readAs) {
			case 'readAsArrayBuffer':
				reader.readAsArrayBuffer(file);
				break;
			case 'readAsDataURL':
				reader.readAsDataURL(file);
				break;
			default:
				reader.readAsText(file);
		}
		return load.pipe(race(error), first());
	}

	static readFileUrl(url: string): Observable<Uint8Array> {
		url = UrlHelper.ignoreProtocol(url);
		const xhr: XMLHttpRequest = UrlHelper.xhr('arraybuffer', 'GET', url);
		xhr.send();
		return observableFromEvent(xhr, 'loadend').pipe(
			first(),
			map(() => {
				if (xhr.status >= 200 && xhr.status < 400) {
					return new Uint8Array(xhr.response);
				} else {
					throw new Error(`${xhr.statusText}: ${url}`);
				}
			})
		);
	}

	static replaceSuffix(fileName: string, divider: string = '.', replaceWith?: string): string {
		// remove the last part of a fileName and (optionally) replace it with something else
		let endIndex = fileName.lastIndexOf(divider);
		if (endIndex > 0) {
			fileName = fileName.substring(0, endIndex);
		}
		if (replaceWith) {
			fileName += divider;
			fileName += replaceWith;
		}
		return fileName;
	}

	static replacePrefix(fileName: string, divider: string = '-', replaceWith?: string): string {
		// remove the first part of a fileName and (optionally) replace it with something else
		const startIndex = fileName.indexOf(divider) + 1;
		if (startIndex > 0) {
			fileName = fileName.substring(startIndex);
		}
		if (replaceWith) {
			fileName = replaceWith + divider + fileName;
		}
		return fileName;
	}

	static getPath(file: File | NamedBlob): string {
		return _.get(file, 'webkitRelativePath', '') || _.get(file, 'fullPath', '') || file.name;
	}

	static getPathExt(path: string): string {
		// get the portion of text after the last period of the path
		const slashIndex: number = path.lastIndexOf('/') + 1;
		const extIndex: number = path.lastIndexOf('.') + 1;
		return extIndex > slashIndex ? path.substr(extIndex) : '';
	}

	static getMimetypeResolution(mimeType: string): string {
		switch (mimeType) {
			case MIMETYPE_X_WAV:
			case MIMETYPE_WAV:
				return '16bit 44.1';
			default:
				return '';
		}
	}

	static getMimetypeExt(mimeType: string): string {
		// convert mimeType to file extention
		switch (mimeType) {
			case MIMETYPE_MPEG:
			case MIMETYPE_MP3:
				return 'mp3';
			case MIMETYPE_X_WAV:
			case MIMETYPE_WAV:
				return 'wav';
			case MIMETYPE_PDF:
				return 'pdf';
			default:
				return '';
		}
	}

	static getMimetypeFromFilename(fileName: string): string {
		const ext: string = FileHelper.getPathExt(fileName);
		switch (ext) {
			case 'mp3':
				return MIMETYPE_MPEG;
			case 'wav':
				return MIMETYPE_X_WAV;
			case 'pdf':
				return MIMETYPE_PDF;
			default:
				return MIMETYPE_APPLICATION;
		}
	}

	static getFilename(path: string, removeSuffix?: string | boolean, removePrefix?: string | boolean): string {
		let prefixChar;
		let suffixChar;
		let fileName = path.substring(path.lastIndexOf('/') + 1);
		if (removeSuffix) {
			if (typeof removeSuffix == 'string') {
				suffixChar = removeSuffix;
			}
			fileName = FileHelper.replaceSuffix(fileName, suffixChar);
		}
		if (removePrefix) {
			if (typeof removePrefix == 'string') {
				prefixChar = removePrefix;
			}
			fileName = FileHelper.replacePrefix(fileName, prefixChar);
		}
		return fileName;
	}

	static getPrettyFilename(path: string, removeSuffix?: string | boolean, removePrefix?: string | boolean): string {
		// remove ID from cloudfront fileName by default
		if (removePrefix === undefined && path.indexOf('.cloudfront.net/') > -1) {
			removePrefix = true;
		}
		const fileName: string = FileHelper.getFilename(path, removeSuffix, removePrefix);
		const reg = new RegExp(/\+/g);
		return decodeURIComponent(fileName).replace(reg, ' ');
	}
}
